// Contrat des marchés publics et fournisseurs (M15, lot L4-C, D-147). Répertoire des fournisseurs (code
// généré, D-038 ; un prestataire du lot L3 peut y être rattaché), procédures paramétrables (libellés,
// étapes, pièces exigées, commission) sans aucun seuil ni règle de la commande publique préremplis (à
// confirmer par l'administration), consultations (numéro CST, D-149) rattachées à un besoin de la
// chaîne de la dépense (metier.besoin, D-180), offres, analyse, attribution, contrats, avenants,
// commandes (numéro CMD), livraisons et réceptions, évaluation du fournisseur en fin de contrat. La
// facture reçue appartient au module finances (metier.facture, D-142) : les achats la créent par le
// contrat commun (depense.ts) avec commande et livraison, et le financier la rattache à une liquidation.
import { z } from 'zod';

const uuid = z.string().uuid('Identifiant invalide');
const jour = z.iso.date('Date AAAA-MM-JJ attendue');
const texte = (max: number, min = 1) => z.string().trim().min(min).max(max);
const facultatif = (max: number) => z.string().trim().max(max).optional();
const pieces = z.array(uuid).max(20).optional();
const limite = z.coerce.number().int().min(1).max(500).default(200);

/** Montant en francs CFA : entier strictement positif. */
export const schemaMontantAchat = z.number().int('Montant entier en francs CFA').positive('Montant strictement positif').max(1_000_000_000_000);

// États --------------------------------------------------------------------------------------------

export const ETATS_CONSULTATION = {
  preparation: 'En préparation',
  publiee: 'Publiée (offres attendues)',
  analyse: 'Offres en analyse',
  attribuee: 'Attribuée',
  infructueuse: 'Infructueuse',
  annulee: 'Annulée',
} as const;
export type EtatConsultation = keyof typeof ETATS_CONSULTATION;
export const CODES_ETATS_CONSULTATION = Object.keys(ETATS_CONSULTATION) as [EtatConsultation, ...EtatConsultation[]];
/** Consultations comptées par ACH-01 (en cours). */
export const ETATS_CONSULTATION_EN_COURS: readonly EtatConsultation[] = ['publiee', 'analyse'];

export const ETATS_OFFRE = { recue: 'Reçue', ecartee: 'Écartée (non conforme)', retenue: 'Retenue', non_retenue: 'Non retenue' } as const;
export type EtatOffre = keyof typeof ETATS_OFFRE;

export const ETATS_CONTRAT = { a_signer: 'À signer', signe: 'Signé (en exécution)', termine: 'Terminé', resilie: 'Résilié' } as const;
export type EtatContrat = keyof typeof ETATS_CONTRAT;
export const CODES_ETATS_CONTRAT = Object.keys(ETATS_CONTRAT) as [EtatContrat, ...EtatContrat[]];

export const ETATS_COMMANDE = { emise: 'Émise', livree_partiellement: 'Livrée en partie', livree: 'Livrée', annulee: 'Annulée' } as const;
export type EtatCommande = keyof typeof ETATS_COMMANDE;
export const CODES_ETATS_COMMANDE = Object.keys(ETATS_COMMANDE) as [EtatCommande, ...EtatCommande[]];

/** Types de numéros attribués par la numérotation existante (D-149, D-181). */
export const TYPES_NUMEROS_ACHATS = { consultation: 'CST', commande: 'CMD', contrat: 'CTR' } as const;

/** Transitions permises d'une consultation (le serveur les contrôle aussi en base). */
export const TRANSITIONS_CONSULTATION: Record<EtatConsultation, readonly EtatConsultation[]> = {
  preparation: ['publiee', 'annulee'],
  publiee: ['analyse', 'infructueuse', 'annulee'],
  analyse: ['attribuee', 'infructueuse', 'annulee'],
  attribuee: [],
  infructueuse: [],
  annulee: [],
};

// Fournisseurs et évaluations ----------------------------------------------------------------------

export const schemaCreationFournisseur = z.strictObject({
  nom: texte(200, 2),
  /** Code explicite (données fictives, tests) ; sinon généré à partir du nom (D-038). */
  code: z.string().trim().regex(/^[A-Za-z0-9][A-Za-z0-9_.-]{0,29}$/, 'Code invalide').optional(),
  identifiant_fiscal: facultatif(40),
  contact: facultatif(200),
  telephone: facultatif(40),
  courriel: z.string().trim().email('Adresse invalide').max(200).optional(),
  adresse: facultatif(300),
  categories: z.array(texte(60)).max(10).default([]),
  /** Prestataire d'intervention du lot L3 rattaché (lien facultatif). */
  prestataire_id: uuid.optional(),
});
export type CreationFournisseur = z.input<typeof schemaCreationFournisseur>;
export const schemaModificationFournisseur = schemaCreationFournisseur.omit({ code: true }).partial().extend({ actif: z.boolean().optional() });
export type ModificationFournisseur = z.infer<typeof schemaModificationFournisseur>;

/** Critères notés de 1 à 5 à l'évaluation de fin de contrat. */
export const CRITERES_EVALUATION = { qualite: 'Qualité des prestations', delais: 'Respect des délais', conformite: 'Conformité aux documents', relation: 'Relation et réactivité' } as const;
export type CritereEvaluation = keyof typeof CRITERES_EVALUATION;
const note = z.number().int().min(1, 'Note de 1 à 5').max(5, 'Note de 1 à 5');
export const schemaEvaluationFournisseur = z.strictObject({
  notes: z.strictObject({ qualite: note, delais: note, conformite: note, relation: note }),
  commentaire: facultatif(2000),
});
export type EvaluationFournisseurSaisie = z.infer<typeof schemaEvaluationFournisseur>;

/** Note globale d'une évaluation : moyenne des critères, une décimale. */
export function noteGlobale(notes: Record<CritereEvaluation, number>): number {
  const v = Object.values(notes);
  return Math.round((v.reduce((s, n) => s + n, 0) / v.length) * 10) / 10;
}

// Procédures paramétrables (aucun seuil prérempli, D-147) -----------------------------------------

export const schemaEtapeProcedure = z.strictObject({
  libelle: texte(120),
  pieces: z.array(texte(120)).max(15).default([]),
});
export const schemaCreationProcedure = z.strictObject({
  libelle: texte(160, 3),
  description: facultatif(2000),
  etapes: z.array(schemaEtapeProcedure).min(1, 'Une étape au moins').max(15),
  pieces_exigees: z.array(texte(120)).max(20).default([]),
  /** Composition de la commission (texte libre), facultative. */
  commission: facultatif(500),
});
export type CreationProcedure = z.input<typeof schemaCreationProcedure>;

// Consultations et offres --------------------------------------------------------------------------

export const schemaCreationConsultation = z.strictObject({
  objet: texte(300, 3),
  /** Besoin de la chaîne de la dépense (validé) à l'origine de l'achat (D-180), facultatif. */
  besoin_id: uuid.optional(),
  procedure_id: uuid,
  estimation: schemaMontantAchat,
  date_limite: jour,
  dossier_document_ids: pieces,
  observation: facultatif(2000),
});
export type CreationConsultation = z.infer<typeof schemaCreationConsultation>;

export const schemaTransitionConsultation = z.strictObject({
  vers: z.enum(['publiee', 'analyse', 'infructueuse', 'annulee']),
  motif: facultatif(1000),
});
export type TransitionConsultation = z.infer<typeof schemaTransitionConsultation>;

export const schemaCreationOffre = z.strictObject({
  fournisseur_id: uuid,
  montant: schemaMontantAchat,
  delai_jours: z.number().int().min(1).max(3650),
  recue_le: z.iso.datetime({ offset: true }).optional(),
  document_ids: pieces,
  observation: facultatif(1000),
});
export type CreationOffre = z.infer<typeof schemaCreationOffre>;

export const schemaAnalyseOffre = z.strictObject({
  conforme: z.boolean(),
  /** Note technique de 0 à 100 attribuée par la commission (offres conformes). */
  note_technique: z.number().min(0).max(100).optional(),
  observation: facultatif(2000),
});
export type AnalyseOffre = z.infer<typeof schemaAnalyseOffre>;

export const schemaAttribution = z.strictObject({ offre_id: uuid, motif: texte(2000, 3) });
export type Attribution = z.infer<typeof schemaAttribution>;

// Contrats, avenants, commandes, livraisons, factures ---------------------------------------------

export const schemaCreationContrat = z
  .strictObject({
    objet: texte(300, 3),
    /** Consultation attribuée : fournisseur et montant repris de l'offre retenue. */
    consultation_id: uuid.optional(),
    fournisseur_id: uuid.optional(),
    montant: schemaMontantAchat.optional(),
    debut: jour,
    fin: jour,
    /** Engagement de la chaîne de la dépense qui couvre le contrat (facultatif). */
    engagement_id: uuid.optional(),
    projet_id: uuid.optional(),
  })
  .refine((v) => Boolean(v.consultation_id) || (Boolean(v.fournisseur_id) && v.montant !== undefined), { message: 'Consultation attribuée, ou fournisseur et montant', path: ['fournisseur_id'] })
  .refine((v) => v.debut <= v.fin, { message: 'La fin suit le début', path: ['fin'] });
export type CreationContrat = z.input<typeof schemaCreationContrat>;

export const schemaSignatureContrat = z.strictObject({ date_signature: jour });
export const schemaCreationAvenant = z
  .strictObject({
    objet: texte(300, 3),
    /** Variation du montant (positive ou négative), en francs CFA. */
    montant_delta: z.number().int().min(-1_000_000_000_000).max(1_000_000_000_000).default(0),
    prolongation_jours: z.number().int().min(0).max(3650).default(0),
    date_signature: jour,
    motif: texte(2000, 3),
  })
  .refine((v) => v.montant_delta !== 0 || v.prolongation_jours > 0, { message: 'Un avenant modifie le montant ou la durée', path: ['montant_delta'] });
export type CreationAvenant = z.input<typeof schemaCreationAvenant>;
export const schemaResiliationContrat = z.strictObject({ motif: texte(1000, 3) });

export const schemaCreationCommande = z
  .strictObject({
    objet: texte(300, 3),
    contrat_id: uuid.optional(),
    fournisseur_id: uuid.optional(),
    montant: schemaMontantAchat,
    date_commande: jour,
    livraison_prevue: jour.optional(),
    engagement_id: uuid.optional(),
  })
  .refine((v) => Boolean(v.contrat_id) || Boolean(v.fournisseur_id), { message: 'Contrat ou fournisseur exigé', path: ['fournisseur_id'] });
export type CreationCommande = z.input<typeof schemaCreationCommande>;

export const schemaCreationLivraison = z.strictObject({
  date_livraison: jour,
  description: texte(1000, 3),
  /** Réception : conforme, ou non conforme avec réserves. */
  conforme: z.boolean(),
  reserves: facultatif(2000),
  /** Dernière livraison attendue de la commande (commande livrée). */
  solde: z.boolean().default(false),
  document_ids: pieces,
}).refine((v) => v.conforme || Boolean(v.reserves), { message: 'Une livraison non conforme porte ses réserves', path: ['reserves'] });
export type CreationLivraison = z.input<typeof schemaCreationLivraison>;

/** Facture reçue d'une commande : créée dans metier.facture par le contrat commun (D-142). */
export const schemaFactureCommande = z.strictObject({
  numero_fournisseur: texte(60),
  date_facture: jour,
  montant: schemaMontantAchat,
  livraison_id: uuid.optional(),
  document_ids: pieces,
});
export type FactureCommande = z.infer<typeof schemaFactureCommande>;

// Filtres ------------------------------------------------------------------------------------------

export const schemaFiltreFournisseurs = z.strictObject({ recherche: facultatif(100), actif: z.enum(['oui', 'non']).optional(), limite });
export const schemaFiltreConsultations = z.strictObject({ etat: z.enum(CODES_ETATS_CONSULTATION).optional(), limite });
export const schemaFiltreContrats = z.strictObject({ etat: z.enum(CODES_ETATS_CONTRAT).optional(), fournisseur_id: uuid.optional(), projet_id: uuid.optional(), limite });
export const schemaFiltreCommandes = z.strictObject({ etat: z.enum(CODES_ETATS_COMMANDE).optional(), contrat_id: uuid.optional(), limite });

// Réponses -----------------------------------------------------------------------------------------

export interface Fournisseur {
  id: string;
  code: string;
  nom: string;
  identifiant_fiscal: string | null;
  contact: string | null;
  telephone: string | null;
  courriel: string | null;
  adresse: string | null;
  categories: string[];
  prestataire: { id: string; nom: string } | null;
  actif: boolean;
  /** Moyenne des notes globales des évaluations (null sans évaluation). */
  note_moyenne: number | null;
  evaluations: number;
  contrats: number;
}

export interface EvaluationFournisseur {
  id: string;
  fournisseur_id: string;
  contrat: { id: string; numero: string; objet: string };
  notes: Record<CritereEvaluation, number>;
  note_globale: number;
  commentaire: string | null;
  evalue_le: string;
  evalue_par: string | null;
}

export interface ProcedureAchat {
  id: string;
  code: string;
  libelle: string;
  description: string | null;
  etapes: { libelle: string; pieces: string[] }[];
  pieces_exigees: string[];
  commission: string | null;
  active: boolean;
}

export interface OffreAchat {
  id: string;
  fournisseur: { id: string; code: string; nom: string };
  montant: number;
  delai_jours: number;
  recue_le: string;
  etat: EtatOffre;
  conforme: boolean | null;
  note_technique: number | null;
  observation: string | null;
  pieces: number;
}

export interface Consultation {
  id: string;
  numero: string;
  objet: string;
  besoin: { id: string; objet: string; etat: string } | null;
  procedure: { id: string; libelle: string; etapes: number };
  estimation: number;
  date_limite: string;
  etat: EtatConsultation;
  observation: string | null;
  motif: string | null;
  offres: OffreAchat[];
  attribution: { offre_id: string; fournisseur_nom: string; montant: number; motif: string; le: string } | null;
  contrat_id: string | null;
  /** Donnée de présentation : montants affichés « (fictif) ». */
  fictif: boolean;
  cree_le: string;
}

export interface Avenant {
  id: string;
  rang: number;
  objet: string;
  montant_delta: number;
  prolongation_jours: number;
  date_signature: string;
  motif: string;
}

export interface ContratAchat {
  id: string;
  numero: string;
  objet: string;
  fournisseur: { id: string; code: string; nom: string };
  consultation: { id: string; numero: string } | null;
  projet: { id: string; numero: string; intitule: string } | null;
  engagement_id: string | null;
  montant_initial: number;
  /** Montant initial augmenté des avenants. */
  montant_actuel: number;
  debut: string;
  fin: string;
  fin_actuelle: string;
  etat: EtatContrat;
  date_signature: string | null;
  avenants: Avenant[];
  evaluation: EvaluationFournisseur | null;
  commande: number;
  facture: number;
  fictif: boolean;
}

export interface Livraison {
  id: string;
  date_livraison: string;
  description: string;
  conforme: boolean;
  reserves: string | null;
  receptionne_par: string | null;
}

export interface CommandeAchat {
  id: string;
  numero: string;
  objet: string;
  contrat: { id: string; numero: string } | null;
  fournisseur: { id: string; code: string; nom: string };
  montant: number;
  date_commande: string;
  livraison_prevue: string | null;
  engagement_id: string | null;
  etat: EtatCommande;
  fictif: boolean;
  livraisons: Livraison[];
  factures: { id: string; numero: string; numero_fournisseur: string; montant: number; etat: string; liquidation: { id: string; numero: string } | null }[];
}

/** Classement des offres conformes : note technique décroissante puis montant croissant. */
export function classerOffres<T extends { etat: EtatOffre; conforme: boolean | null; note_technique: number | null; montant: number }>(offres: T[]): T[] {
  return offres
    .filter((o) => o.conforme !== false && o.etat !== 'ecartee')
    .sort((a, b) => (b.note_technique ?? -1) - (a.note_technique ?? -1) || a.montant - b.montant);
}
